import type { Dayjs } from 'dayjs';
import type { TextFieldProps } from '@mui/material/TextField';
import type { DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import type { MobileDateTimePickerProps } from '@mui/x-date-pickers/MobileDateTimePicker';

import dayjs from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';

import { Box } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { formatPatterns } from 'src/utils/format-time';

// ----------------------------------------------------------------------

type RHFDatePickerProps = DatePickerProps<Dayjs> & {
  name: string;
  endAdornment?: any;
};

export function RHFDatePicker({ name, endAdornment, slotProps, ...other }: RHFDatePickerProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <DatePicker
            {...field}
            value={dayjs(field.value)}
            onChange={(newValue) => field.onChange(dayjs(newValue).format())}
            format={formatPatterns.split.date}
            slotProps={{
              ...slotProps,
              textField: {
                fullWidth: true,
                error: !!error,
                helperText: error?.message ?? (slotProps?.textField as TextFieldProps)?.helperText,
                ...slotProps?.textField,
              },
            }}
            {...other}
          />
          {endAdornment}
        </Box>
      )}
    />
  );
}

// ----------------------------------------------------------------------

type RHFMobileDateTimePickerProps = MobileDateTimePickerProps<Dayjs> & {
  name: string;
  localeText?: any; // Add this prop
  t?: any;
};

export function RHFMobileDateTimePicker({
  t = (label: string) => label,
  name,
  slotProps,
  localeText,
  ...other
}: RHFMobileDateTimePickerProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale="en"
          localeText={{
            cancelButtonLabel: `${t('CANCEL')}`,
            okButtonLabel: `${t('SAVE')}`,
            dateTimePickerToolbarTitle: `${t('SCHEDULE_DATETIME')}`,
            openPreviousView: `${t('OPEN_PREVIOUS')}`,
            openNextView: `${t('OPEN_NEXT')}`,
            nextMonth: `${t('NEXT_MONTH')}`,
            previousMonth: `${t('PREVIOUS_MONTH')}`,
          }}
        >
          <MobileDateTimePicker
            {...field}
            value={dayjs(field.value)}
            onChange={(newValue) => field.onChange(dayjs(newValue).format())}
            format={formatPatterns.split.dateTime}
            slotProps={{
              actionBar: {
                actions: ['cancel', 'accept'],
              },
              textField: {
                fullWidth: true,
                error: !!error,
                helperText: error?.message ?? (slotProps?.textField as TextFieldProps)?.helperText,
                ...slotProps?.textField,
              },
              ...slotProps,
            }}
            {...other}
          />
        </LocalizationProvider>
      )}
    />
  );
}
